// react
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// components
import {
  AdminInviteFormPage,
  AdminItem,
} from '@karehero/llama/pwa/pages/AdminInviteFormPage/AdminInviteFormPage';

// hooks
import { useGoBack } from 'hooks/useGoBack';

// store
import {
  useGetSessionOrganizationQuery,
  useGetOrganizationAccountsQuery,
  useInviteOrganizationAdminMutation,
} from 'store/api/organization';
import { newToast } from 'store/slices/uiSlice';

// router
import { ROUTES } from 'router';

/**
 * AdminInvite wires the AdminInviteFormPage to the invite org admin API
 */
export const AdminInvite = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const { data: organization } = useGetSessionOrganizationQuery();
  const { data: owners } = useGetOrganizationAccountsQuery(
    { organizationID: organization?.id || '', role: 'admin' },
    { skip: !organization?.id },
  );
  const [inviteOrganizationAdmin, { isLoading }] =
    useInviteOrganizationAdminMutation();

  const administrators = useMemo<AdminItem[]>(() => {
    if (!owners) return [];
    return owners.map((owner) => ({
      name:
        owner.fullName ||
        `${owner.firstName || ''} ${owner.lastName || ''}`.trim(),
      status: (owner.isRegistered ? 'active' : 'pending') as
        'active' | 'pending',
    }));
  }, [owners]);

  const onSubmit = useCallback(
    async (data: { firstName: string; lastName: string; email: string }) => {
      if (!organization) return;
      const res = await inviteOrganizationAdmin({
        ...data,
        organizationID: organization.id,
      });
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to send organisation admin invite.',
          }),
        );
        return;
      }
      navigate(ROUTES.PORTAL_ADMIN_INVITE_SENT, {
        replace: true,
        state: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        },
      });
    },
    [organization, inviteOrganizationAdmin, dispatch, navigate],
  );

  return (
    <AdminInviteFormPage
      onBack={goBack}
      onSubmit={onSubmit}
      administrators={administrators}
      isSubmitting={isLoading}
    />
  );
};

export default AdminInvite;
